import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Pause, Play, Send, XCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Pill, TrustBadge, trustFor, Table, Stat } from "./ui";

function useResource(path) {
  const [data, setData] = useState(null);
  const load = useCallback(() => { api.get(path).then((r) => setData(r.data)).catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setData({}); }); }, [path]);
  useEffect(() => { load(); }, [load]);
  return [data, load];
}

const list = (d) => (Array.isArray(d) ? d : []);

/* ---------- COMMAND CENTER ---------- */
export function AdminActivity() {
  const [d] = useResource("/admin/activity");
  const rows = d ? list(d.events || d) : null;
  return (<div data-testid="page-activity">
    <PageHeader kicker="Command Center" title="LIVE ACTIVITY" sub="Real acquisition and outreach events as they happen — persisted in the database, never frontend state." />
    <Table testid="activity-table" rows={rows} empty="No activity yet — run a discovery to see live events."
      columns={[
        { label: "Time", render: (r) => <span className="font-mono text-[11px]">{String(r.ts || "").slice(5, 19).replace("T", " ")}</span> },
        { label: "Event", render: (r) => <Pill value={(r.type || "").split(".")[1]?.toUpperCase() || r.type} /> },
        { label: "Detail", key: "message" },
        { label: "Object", render: (r) => <span className="font-mono text-[11px] text-[#7C8AA3]">{r.company || r.token || "—"}</span> },
      ]} />
  </div>);
}

/* ---------- DISCOVERY ---------- */
export function AdminCompanies() {
  const [d] = useResource("/admin/prospects");
  return (<div data-testid="page-companies">
    <PageHeader kicker="Discovery" title="COMPANIES" sub="Master company records with entity resolution — duplicates merged across sources." />
    <Table testid="companies-table" rows={d} empty="No companies discovered yet — start with Global Search."
      columns={[
        { label: "Company", render: (r) => <span className="font-semibold text-[#0B1B33]">{r.company_name}</span> },
        { label: "Industry", key: "industry" },
        { label: "Location", render: (r) => [r.city, r.country].filter(Boolean).join(", ") || "—" },
        { label: "Website", render: (r) => r.website_verified ? <span className="text-emerald-700 text-[11px] font-mono">✓ VERIFIED</span> : <span className="text-amber-600 text-[11px] font-mono">PENDING</span> },
        { label: "Status", render: (r) => <Pill value={r.status} /> },
        { label: "Trust", render: (r) => <TrustBadge type={trustFor(r)} /> },
        { label: "Score", render: (r) => <span className="font-mono font-bold">{r.ai_score ?? "—"}</span> },
      ]} />
  </div>);
}

export function AdminContacts() {
  const [d] = useResource("/admin/prospects");
  const rows = d ? d.filter((p) => p.contact_email) : null;
  return (<div data-testid="page-contacts">
    <PageHeader kicker="Discovery" title="CONTACTS" sub="Lawful public business contacts only — role-based addresses from verified company websites. No personal inboxes, no purchased lists." />
    <Table testid="contacts-table" rows={rows} empty="No public business contacts discovered yet."
      columns={[
        { label: "Email", render: (r) => <span className="font-mono text-[12px]">{r.contact_email}</span> },
        { label: "Company", key: "company_name" },
        { label: "Validation", render: () => <Pill value="VERIFIED" /> },
        { label: "Suppression", render: () => <span className="text-emerald-700 text-[11px] font-mono">CLEAR</span> },
        { label: "Status", render: (r) => <Pill value={r.status} /> },
        { label: "Source", render: (r) => <span className="text-[11px] font-mono text-[#7C8AA3]">{r.source || "discovery"}</span> },
      ]} />
  </div>);
}

export function AdminVerification() {
  const [d] = useResource("/admin/prospects");
  return (<div data-testid="page-verification">
    <PageHeader kicker="Discovery" title="VERIFICATION" sub="Every company passes: website found → domain check → identity check → official website verification. Nothing is labelled verified without evidence." />
    <Table testid="verification-table" rows={d} empty="No companies in the verification pipeline."
      columns={[
        { label: "Company", render: (r) => <span className="font-semibold">{r.company_name}</span> },
        { label: "Website", render: (r) => <span className="font-mono text-[11px] text-[#2563EB]">{r.website || "—"}</span> },
        { label: "Verification", render: (r) => r.website_verified
            ? <span className="flex items-center gap-1.5 text-emerald-700 text-[11px] font-mono"><CheckCircle2 size={13} /> OFFICIAL WEBSITE VERIFIED</span>
            : <span className="flex items-center gap-1.5 text-amber-600 text-[11px] font-mono"><XCircle size={13} /> {r.status === "VERIFICATION_FAILED" ? "FAILED" : "UNDER REVIEW"}</span> },
        { label: "Confidence", render: (r) => <Pill value={r.website_verified ? "VERIFIED" : "PENDING"} /> },
        { label: "Last Verified", render: (r) => <span className="font-mono text-[11px] text-[#7C8AA3]">{String(r.updated_at || "").slice(0, 16).replace("T", " ")}</span> },
        { label: "Source", render: (r) => <span className="text-[11px] font-mono text-[#7C8AA3]">{r.source || "discovery"}</span> },
      ]} />
  </div>);
}

export function AdminSources() {
  const [d] = useResource("/admin/sources");
  const rows = d ? d.sources : null;
  return (<div data-testid="page-sources">
    <PageHeader kicker="Discovery" title="SOURCES" sub="Actual coverage — no 'entire internet' claims. Source provenance is stored for every record." />
    {d && <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
      <Stat label="Sources Used" value={d.sources?.length ?? 0} testid="sources-count" />
      <Stat label="Total Records" value={d.total_records ?? 0} testid="sources-records" />
      <Stat label="Approved Imports" value={(d.sources || []).filter((s) => /gov|mpeda|apeda/i.test(s.source)).length} testid="sources-gov" hint="MPEDA / government directories" />
    </div>}
    <Table testid="sources-table" rows={rows} empty="No source data yet."
      columns={[
        { label: "Source", render: (r) => <span className="font-mono text-[12px] font-semibold">{r.source}</span> },
        { label: "Records", render: (r) => <span className="font-mono font-bold">{r.count}</span> },
        { label: "Type", render: (r) => <TrustBadge type={/gov|mpeda|apeda/i.test(r.source) ? "VERIFIED" : r.source === "manual" ? "USER PROVIDED" : "AI-GENERATED"} /> },
      ]} />
  </div>);
}

/* ---------- INTELLIGENCE ---------- */
export function AdminScores() {
  const [d] = useResource("/admin/prospects");
  const rows = d ? [...d].filter((p) => p.ai_score != null).sort((a, b) => b.ai_score - a.ai_score) : null;
  return (<div data-testid="page-scores">
    <PageHeader kicker="Intelligence" title="OPPORTUNITY SCORES" sub="10X Opportunity Score — AI-ranked on available evidence only. Scores never fabricate revenue, relationships or activity." />
    <Table testid="scores-table" rows={rows} empty="No scored companies yet."
      columns={[
        { label: "Company", render: (r) => <span className="font-semibold">{r.company_name}</span> },
        { label: "Industry", key: "industry" },
        { label: "10X Score", render: (r) => <span className="font-mono font-bold bg-gradient-to-r from-[#2563EB] to-[#7C3AED] bg-clip-text text-transparent">{r.ai_score}/100</span> },
        { label: "AI Reason", render: (r) => <span className="text-[11.5px] text-[#5B6B87] max-w-[320px] block truncate" title={r.ai_reason}>{r.ai_reason || "—"}</span> },
        { label: "Trust", render: () => <TrustBadge type="AI-GENERATED" /> },
        { label: "Status", render: (r) => <Pill value={r.status} /> },
      ]} />
  </div>);
}

export function AdminMarket() {
  const [d] = useResource("/admin/market-data");
  const cols = [
    { label: "Name", render: (r) => <span className="font-semibold">{r.name}</span> },
    { label: "Companies", render: (r) => <span className="font-mono font-bold">{r.companies}</span> },
    { label: "Verified", render: (r) => <span className="font-mono text-emerald-700">{r.verified}</span> },
    { label: "With Public Contact", render: (r) => <span className="font-mono">{r.with_contacts ?? "—"}</span> },
    { label: "Avg Opportunity", render: (r) => <span className="font-mono font-bold bg-gradient-to-r from-[#2563EB] to-[#7C3AED] bg-clip-text text-transparent">{r.avg_score}</span> },
    { label: "Basis", render: () => <TrustBadge type="VERIFIED" /> },
  ];
  return (<div data-testid="page-market">
    <PageHeader kicker="Intelligence" title="MARKET INTELLIGENCE" sub="Live aggregates computed from your real company pipeline — no invented demand figures. AI signals stay labelled separately." />
    <p className="mb-4 flex items-center gap-2 text-[11px] text-[#5B6B87]" data-testid="market-basis"><TrustBadge type="VERIFIED" /> {d?.basis || "Computed live from pipeline records"}</p>
    <p className="adm-kicker mb-2">By Industry</p>
    <Table testid="market-industries-table" rows={d ? d.industries : null} columns={cols} empty="No industry data yet — discovery runs populate this." />
    <p className="adm-kicker mt-8 mb-2">By Country / Region</p>
    <Table testid="market-regions-table" rows={d ? d.regions : null} columns={cols} empty="No regional data yet." />
  </div>);
}

export function AdminInsights() {
  const [recs] = useResource("/admin/recommendations");
  const [fc] = useResource("/admin/forecast");
  return (<div data-testid="page-insights">
    <PageHeader kicker="Intelligence" title="AI INSIGHTS" sub="Recommendations and forecasts generated from your real pipeline data. Labelled AI-GENERATED — review before acting." />
    {recs && <div className="adm-card p-5 mb-5" data-testid="recommendations-card">
      <p className="adm-kicker mb-3">Recommendations</p>
      {(recs.recommendations || list(recs)).length === 0 ? <p className="text-[12px] text-[#93A3BC]">No recommendations yet.</p> :
        <ul className="space-y-2">{(recs.recommendations || list(recs)).map((r, i) => (
          <li key={i} className="text-[12.5px] text-[#22355A] flex gap-2"><span className="text-[#7C3AED]">◆</span>{typeof r === "string" ? r : r.text || r.message || JSON.stringify(r)}</li>))}
        </ul>}
    </div>}
    {fc && <div className="adm-card p-5" data-testid="forecast-card">
      <p className="adm-kicker mb-3">Forecast</p>
      <pre className="text-[11.5px] font-mono text-[#43546D] whitespace-pre-wrap">{JSON.stringify(fc, null, 2)}</pre>
    </div>}
    {!recs && !fc && <Table rows={null} columns={[]} testid="insights-loading" />}
  </div>);
}

/* ---------- OUTREACH ---------- */
export function AdminCampaigns() {
  const [d, load] = useResource("/admin/runs");
  const [working, setWorking] = useState("");
  const stop = async (runId) => {
    setWorking(runId);
    try { await api.post(`/admin/runs/${runId}/stop`); toast.success("Run stopped"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWorking(""); }
  };
  const rows = d ? list(d.runs || d) : null;
  return (<div data-testid="page-campaigns">
    <PageHeader kicker="Outreach" title="CAMPAIGNS" sub="Discovery and outreach runs — each with stage tracking and real outcome counts." />
    <Table testid="campaigns-table" rows={rows} empty="No campaigns yet — run one from Global Search."
      columns={[
        { label: "Campaign", render: (r) => <span className="font-semibold">{r.name}</span> },
        { label: "Source", render: (r) => <span className="font-mono text-[11px]">{r.source}</span> },
        { label: "Status", render: (r) => <Pill value={r.status} /> },
        { label: "Discovered / Verified / Invited", render: (r) => <span className="font-mono text-[11.5px]">{r.counts?.discovered ?? 0} / {r.counts?.verified ?? 0} / {r.counts?.invited ?? 0}</span> },
        { label: "Created", render: (r) => <span className="font-mono text-[11px] text-[#7C8AA3]">{String(r.created_at || "").slice(0, 10)}</span> },
        { label: "", render: (r) => r.status === "RUNNING" ? <button onClick={() => stop(r.run_id)} disabled={!!working} data-testid={`stop-run-${r.run_id}`} className="adm-btn-ghost !px-3 !py-1.5">{working === r.run_id ? <Loader2 size={11} className="animate-spin" /> : "Stop"}</button> : null },
      ]} />
  </div>);
}

export function AdminInvitations() {
  const [d] = useResource("/admin/prospects");
  const rows = d ? d.filter((p) => ["INVITED", "VIEWED", "REQUESTED", "MEMBER"].includes(p.status)) : null;
  return (<div data-testid="page-invitations">
    <PageHeader kicker="Outreach" title="INVITATIONS" sub="Invitations marked sent only after provider acceptance — with real provider message IDs." />
    <Table testid="invitations-table" rows={rows} empty="No invitations sent yet."
      columns={[
        { label: "Company", render: (r) => <span className="font-semibold">{r.company_name}</span> },
        { label: "Contact", render: (r) => <span className="font-mono text-[11.5px]">{r.contact_email}</span> },
        { label: "Status", render: (r) => <Pill value={r.delivery_status || r.status} /> },
        { label: "Provider ID", render: (r) => <span className="font-mono text-[10.5px] text-[#7C8AA3] break-all">{r.email_id || "—"}</span> },
        { label: "Sent", render: (r) => <span className="font-mono text-[11px] text-[#7C8AA3]">{String(r.invited_at || "").slice(0, 16).replace("T", " ")}</span> },
      ]} />
  </div>);
}

export function AdminQueue() {
  const [d] = useResource("/admin/queue");
  return (<div data-testid="page-queue">
    <PageHeader kicker="Outreach" title="EMAIL QUEUE" sub="Retry-safe outreach queue. Every row passed the eligibility engine: verified → valid contact → not suppressed → not duplicate → campaign eligible." />
    {d?.counts && <div className="flex flex-wrap gap-2 mb-4" data-testid="queue-counts">
      {Object.entries(d.counts).map(([k, v]) => <span key={k} className="adm-card px-3 py-2 flex items-center gap-2"><Pill value={k} /><span className="font-mono font-bold text-[13px]">{v}</span></span>)}
    </div>}
    <Table testid="queue-table" rows={d ? d.queue : null} empty="Queue is empty — nothing scheduled or sent."
      columns={[
        { label: "Company", render: (r) => <span className="font-semibold">{r.company}</span> },
        { label: "Contact", render: (r) => <span className="font-mono text-[11px]">{r.contact}</span> },
        { label: "Campaign", render: (r) => <span className="font-mono text-[10.5px] text-[#7C8AA3]">{r.campaign}</span> },
        { label: "Eligibility", render: (r) => <Pill value={r.eligibility || "QUEUED"} /> },
        { label: "Status", render: (r) => <Pill value={r.status} /> },
        { label: "Attempts", render: (r) => <span className="font-mono">{r.attempts}</span> },
        { label: "Provider ID", render: (r) => <span className="font-mono text-[10px] text-[#7C8AA3] break-all">{r.provider_id || "—"}</span> },
        { label: "Sent", render: (r) => <span className="font-mono text-[11px] text-[#7C8AA3]">{String(r.sent || "").slice(0, 16).replace("T", " ") || "—"}</span> },
      ]} />
  </div>);
}

export function AdminDelivery() {
  const [d] = useResource("/admin/delivery");
  return (<div data-testid="page-delivery">
    <PageHeader kicker="Outreach" title="DELIVERY" sub="Live provider events — webhook-received and provider-polled. The database is the source of truth, never the UI." />
    {d?.counts && <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 mb-5" data-testid="delivery-counts">
      {Object.entries(d.counts).map(([k, v]) => <div key={k} className="adm-card p-3 text-center"><p className="font-mono font-bold text-lg">{v}</p><Pill value={k} /></div>)}
    </div>}
    <Table testid="delivery-table" rows={d ? d.events : null} empty="No delivery events yet — events appear after sends are delivered, opened or bounced."
      columns={[
        { label: "Time", render: (r) => <span className="font-mono text-[11px]">{String(r.ts || "").slice(5, 19).replace("T", " ")}</span> },
        { label: "Event", render: (r) => <Pill value={r.status} /> },
        { label: "Recipient", render: (r) => <span className="font-mono text-[11.5px]">{r.recipient || "—"}</span> },
        { label: "Company", key: "company" },
        { label: "Source", render: (r) => <span className="font-mono text-[10.5px] text-[#7C8AA3]">{r.source || "webhook"}</span> },
      ]} />
  </div>);
}

export function AdminResponses() {
  const [d] = useResource("/admin/responses");
  const rows = d ? d.replies : null;
  return (<div data-testid="page-responses">
    <PageHeader kicker="Outreach" title="RESPONSE CENTER" sub="Inbound replies matched to contact, company and campaign, then classified: interested, meeting, not interested, unsubscribe, wrong contact, out of office." />
    {rows && rows.length === 0 ? (
      <div className="adm-card p-8 text-center" data-testid="responses-not-configured">
        <p className="text-[13px] text-[#5B6B87] font-medium">No responses yet.</p>
        <p className="mt-2 text-[11.5px] text-[#93A3BC] max-w-md mx-auto">{d?.note || "Reply processing activates after inbound email is configured."} When a recipient asks for no further contact, suppression is applied automatically.</p>
      </div>
    ) : (
      <Table testid="responses-table" rows={rows} empty="No responses."
        columns={[
          { label: "From", key: "from" }, { label: "Company", key: "company" },
          { label: "Classification", render: (r) => <Pill value={r.classification || "UNKNOWN"} /> },
          { label: "Received", render: (r) => <span className="font-mono text-[11px]">{String(r.ts || "").slice(0, 16).replace("T", " ")}</span> },
        ]} />
    )}
  </div>);
}

export function AdminSuppression() {
  const [d] = useResource("/admin/suppression");
  return (<div data-testid="page-suppression">
    <PageHeader kicker="Outreach" title="SUPPRESSION" sub="Opt-outs and provider-confirmed dead addresses. Suppressed contacts are never emailed again — enforced at send time." />
    {d && <p className="mb-4 text-[12px] text-[#5B6B87]" data-testid="suppression-count"><span className="font-mono font-bold text-[#0B1B33]">{d.count}</span> addresses suppressed</p>}
    <Table testid="suppression-table" rows={d ? d.suppressed : null} empty="No suppressed addresses — the list grows via opt-outs and provider bounces/complaints."
      columns={[
        { label: "Email", render: (r) => <span className="font-mono text-[12px]">{r.email}</span> },
        { label: "Reason", render: (r) => <Pill value={(r.reason || "opt_out").toUpperCase()} /> },
        { label: "When", render: (r) => <span className="font-mono text-[11px] text-[#7C8AA3]">{String(r.ts || "").slice(0, 16).replace("T", " ")}</span> },
      ]} />
  </div>);
}

/* ---------- MEMBERSHIP ---------- */
export function AdminProspects() {
  const [d] = useResource("/admin/prospects");
  return (<div data-testid="page-prospects">
    <PageHeader kicker="Membership" title="PROSPECTS" sub="Full pipeline: discovered → verified → invited → engaged → application → approved → active member." />
    <Table testid="prospects-table" rows={d} empty="No prospects yet."
      columns={[
        { label: "Company", render: (r) => <span className="font-semibold">{r.company_name}</span> },
        { label: "Industry", key: "industry" },
        { label: "Pipeline", render: (r) => <Pill value={r.status} /> },
        { label: "Delivery", render: (r) => r.delivery_status ? <Pill value={r.delivery_status} /> : <span className="text-[#93A3BC] text-[11px]">—</span> },
        { label: "Score", render: (r) => <span className="font-mono font-bold">{r.ai_score ?? "—"}</span> },
        { label: "Trust", render: (r) => <TrustBadge type={trustFor(r)} /> },
        { label: "Created", render: (r) => <span className="font-mono text-[11px] text-[#7C8AA3]">{String(r.created_at || "").slice(0, 10)}</span> },
      ]} />
  </div>);
}

export function AdminApplications() {
  const [d] = useResource("/admin/approvals");
  const rows = d ? list(d.approvals || d.sessions || d) : null;
  return (<div data-testid="page-applications">
    <PageHeader kicker="Membership" title="APPLICATIONS" sub="Membership applications and payment-verified registrations awaiting admin action." />
    <Table testid="applications-table" rows={rows} empty="No applications pending."
      columns={[
        { label: "Applicant", render: (r) => <span className="font-semibold">{r.name || r.company_name || r.email || "—"}</span> },
        { label: "Email", render: (r) => <span className="font-mono text-[11.5px]">{r.email || "—"}</span> },
        { label: "Plan", render: (r) => r.plan_id || r.plan || "—" },
        { label: "Payment", render: (r) => <Pill value={(r.payment_status || "PENDING").toUpperCase()} /> },
        { label: "Approval", render: (r) => <Pill value={(r.approval_status || "PENDING").toUpperCase()} /> },
        { label: "Date", render: (r) => <span className="font-mono text-[11px] text-[#7C8AA3]">{String(r.created_at || "").slice(0, 10)}</span> },
      ]} />
  </div>);
}

export function AdminApproval() {
  const [d, load] = useResource("/admin/approvals");
  const [working, setWorking] = useState("");
  const rows = d ? list(d.approvals || d.sessions || d).filter((a) => (a.approval_status || "PENDING") !== "APPROVED") : null;
  const act = async (sessionId, action) => {
    setWorking(sessionId + action);
    try { await api.post(`/admin/approvals/${sessionId}/${action}`, action === "reject" ? { reason: "Admin review" } : {}); toast.success(`Application ${action}d`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWorking(""); }
  };
  return (<div data-testid="page-approval">
    <PageHeader kicker="Membership" title="APPROVAL QUEUE" sub="Every decision is logged. Approve, hold or reject — applicants are notified by email." />
    <Table testid="approval-table" rows={rows} empty="No applications awaiting review."
      columns={[
        { label: "Applicant", render: (r) => <span className="font-semibold">{r.name || r.company_name || r.email || "—"}</span> },
        { label: "Verification", render: (r) => <Pill value={(r.payment_status || "PENDING").toUpperCase()} /> },
        { label: "AI Recommendation", render: () => <span className="text-[11px] text-[#93A3BC]">—</span> },
        { label: "Risk Flags", render: (r) => r.risk_flags?.length ? <Pill value="REVIEW REQUIRED" /> : <span className="text-emerald-700 text-[11px] font-mono">CLEAR</span> },
        { label: "Actions", render: (r) => (
          <div className="flex gap-1.5">
            <button onClick={() => act(r.session_id || r.id, "approve")} disabled={!!working} data-testid={`approve-${r.session_id || r.id}`} className="adm-btn !px-3 !py-1.5">{working === (r.session_id || r.id) + "approve" ? <Loader2 size={11} className="animate-spin" /> : "Approve"}</button>
            <button onClick={() => act(r.session_id || r.id, "reject")} disabled={!!working} data-testid={`reject-${r.session_id || r.id}`} className="adm-btn-ghost !px-3 !py-1.5 !text-red-600 !border-red-200">Reject</button>
          </div>) },
      ]} />
  </div>);
}

export function AdminMembers() {
  const [d] = useResource("/admin/members");
  return (<div data-testid="page-members">
    <PageHeader kicker="Membership" title="ACTIVE MEMBERS" sub="Approved companies with live 10XGLOBAL membership." />
    {d && <div className="grid grid-cols-2 gap-3 mb-5">
      <Stat label="Network Members" value={d.count} testid="members-count" />
      <Stat label="Active User Memberships" value={d.active_users} testid="members-users" />
    </div>}
    <Table testid="members-table" rows={d ? d.members : null} empty="No active members yet — the pipeline converts prospects into members."
      columns={[
        { label: "Company", render: (r) => <span className="font-semibold">{r.company_name}</span> },
        { label: "Industry", key: "industry" },
        { label: "Location", render: (r) => [r.city, r.country].filter(Boolean).join(", ") || "—" },
        { label: "Status", render: (r) => <Pill value="MEMBER" /> },
        { label: "Since", render: (r) => <span className="font-mono text-[11px] text-[#7C8AA3]">{String(r.updated_at || "").slice(0, 10)}</span> },
      ]} />
  </div>);
}

/* ---------- SYSTEM ---------- */
export function AdminAutomation() {
  const [d, load] = useResource("/admin/automation/status");
  const [working, setWorking] = useState(false);
  const ready = d && (d.state || "").includes("READY");
  const setMode = async (mode) => {
    setWorking(true);
    try { await api.post("/admin/automation/mode", { mode }); toast.success(`Mode set: ${mode}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWorking(false); }
  };
  return (<div data-testid="page-automation">
    <PageHeader kicker="System" title="AUTOMATION" sub="Frontend controls never bypass backend safety rules — the eligibility engine gates every send." />
    {d ? (
      <div className="space-y-5">
        <div className="adm-card p-5 flex flex-wrap items-center justify-between gap-4" data-testid="automation-status-card">
          <div>
            <p className="adm-kicker mb-1.5">Automation Status</p>
            <p className={`text-lg font-bold font-mono ${ready ? "text-emerald-700" : "text-amber-600"}`} data-testid="automation-state">{d.state}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setMode("MANUAL")} disabled={working} data-testid="pause-automation-btn" className="adm-btn-ghost"><Pause size={12} /> Pause Automation</button>
            <button onClick={() => setMode("CONTROLLED_AUTO_SEND")} disabled={working || !d.can_enable_auto_send} data-testid="resume-automation-btn" className="adm-btn"><Play size={12} /> Controlled Auto</button>
          </div>
        </div>
        <div className="adm-card p-5" data-testid="automation-modes">
          <p className="adm-kicker mb-3">Operation Mode</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[["MANUAL", "You review and send every invitation"], ["AUTO_DRAFT", "AI drafts — you approve before send"], ["CONTROLLED_AUTO_SEND", "Eligibility-gated automatic sends"]].map(([v, label]) => (
              <button key={v} onClick={() => setMode(v)} disabled={working || (v === "CONTROLLED_AUTO_SEND" && !d.can_enable_auto_send)}
                data-testid={`automation-mode-${v.toLowerCase()}`}
                className={`text-left border rounded-lg px-4 py-3.5 transition-colors ${d.mode === v ? "border-[#2563EB] bg-[#2563EB]/5" : "border-[#E4EAF3] hover:border-[#2563EB]/40"}`}>
                <p className="text-[12px] font-bold text-[#0B1B33]">{v.replace(/_/g, " ")}</p>
                <p className="text-[11px] text-[#7C8AA3] mt-1">{label}</p>
              </button>
            ))}
          </div>
          {!d.can_enable_auto_send && <p className="mt-3 text-[11.5px] text-amber-600 font-mono" data-testid="auto-send-blocked">Controlled auto-send locked — complete the production checklist first (API/Webhooks).</p>}
        </div>
      </div>
    ) : <Table rows={null} columns={[]} testid="automation-loading" />}
  </div>);
}

export function AdminEmailInfra() {
  const [d, load] = useResource("/admin/email/health");
  const [otpLogs] = useResource("/admin/otp-logs");
  const [otpStats] = useResource("/admin/otp-stats");
  const [to, setTo] = useState("");
  const [test, setTest] = useState(null);
  const [working, setWorking] = useState(false);
  const sendTest = async () => {
    setWorking(true);
    try {
      const { data } = await api.post("/admin/email/send-test", { to });
      const check = await api.post("/admin/email/verify-delivery", { email_id: data.email_id });
      setTest({ ...check.data, email_id: data.email_id });
      load();
    } catch (e) { setTest({ error: formatApiErrorDetail(e.response?.data?.detail) }); }
    finally { setWorking(false); }
  };
  const gates = d ? Object.entries(d.gates || d.checks || {}) : [];
  return (<div data-testid="page-email-infra">
    <PageHeader kicker="System" title="EMAIL INFRASTRUCTURE" sub="Production provider health. Credentials live only in server-side secrets — never in the browser." />
    {d ? (
      <div className="space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="email-health-gates">
          {(gates.length ? gates : Object.entries(d).filter(([, v]) => typeof v === "boolean")).map(([k, v]) => (
            <div key={k} className="adm-card p-4 flex items-center gap-2.5" data-testid={`infra-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {(v === true || v?.passed) ? <CheckCircle2 size={15} className="text-emerald-600" /> : <XCircle size={15} className="text-amber-500" />}
              <div><p className="text-[11px] font-semibold uppercase tracking-wide text-[#0B1B33]">{k.replace(/_/g, " ")}</p>
                <p className="text-[10px] font-mono text-[#7C8AA3]">{(v === true || v?.passed) ? "Connected ✓" : "Check required"}</p></div>
            </div>
          ))}
        </div>
        <div className="adm-card p-5" data-testid="email-test-panel">
          <p className="adm-kicker mb-3">Delivery Test</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="test inbox address (e.g. delivered@resend.dev)" data-testid="test-email-input" className="adm-input flex-1" />
            <button onClick={sendTest} disabled={working || !to.includes("@")} data-testid="send-test-btn" className="adm-btn">{working ? <Loader2 size={13} className="animate-spin" /> : <><Send size={12} /> Send & Verify</>}</button>
          </div>
          {test && (
            <div className={`mt-3 border rounded-lg p-3.5 text-[12px] font-mono ${test.verified ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid="test-email-result">
              {test.error ? `ERROR — ${test.error}` : test.verified ? `✓ Provider-confirmed DELIVERED (id: ${test.email_id})` : `Provider status: ${test.provider_status || test.reason || "pending — verify again in a few seconds"}`}
            </div>
          )}
        </div>
        {/* OTP DELIVERY LOGS */}
        <div className="adm-card p-5" data-testid="otp-logs-section">
          <p className="adm-kicker mb-3">OTP Delivery Logs — Password Reset (OTP values are never stored or displayed)</p>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2.5 mb-4" data-testid="otp-stats-grid">
            {[["OTP Sent", "sent"], ["Delivered", "delivered"], ["Failed", "failed"], ["Verified", "verified"], ["Expired", "expired"], ["Resends", "resends"]].map(([label, key]) => (
              <div key={key} className="border border-[#E4EAF3] rounded-lg p-3 text-center" data-testid={`otp-stat-${key}`}>
                <p className="text-lg font-bold font-mono text-[#0B1B33]">{otpStats ? otpStats[key] : "—"}</p>
                <p className="text-[9.5px] font-mono uppercase tracking-widest text-[#7C8AA3] mt-0.5">{label}</p>
              </div>
            ))}
          </div>
          <Table testid="otp-logs-table" rows={otpLogs ? otpLogs.logs : null} empty="No OTP events yet."
            columns={[
              { label: "Timestamp", render: (r) => <span className="font-mono text-[11px]">{(r.created_at || "").replace("T", " ").slice(0, 19)}</span> },
              { label: "Member", render: (r) => <span className="font-mono text-[11.5px]">{r.email_masked || "—"}</span> },
              { label: "Channel", render: (r) => <span className="font-mono text-[10.5px] uppercase text-[#43546D]">{r.channel || "email"}</span> },
              { label: "Mobile", render: (r) => <span className="font-mono text-[11px]">{r.mobile_masked || "—"}</span> },
              { label: "Member ID", render: (r) => <span className="font-mono text-[10.5px] text-[#7C8AA3]">{r.user_id ? r.user_id.slice(-8) : "—"}</span> },
              { label: "Status", render: (r) => <Pill value={r.status} /> },
              { label: "Provider Message ID", render: (r) => <span className="font-mono text-[10.5px] text-[#2563EB]">{r.provider_message_id || "—"}</span> },
              { label: "Provider Response", render: (r) => <span className="text-[11px] text-[#5B6B87]">{r.provider_response || "—"}</span> },
              { label: "Resend #", render: (r) => <span className="font-mono text-[11px]">{r.resend_count ?? 0}</span> },
              { label: "Attempts", render: (r) => <span className="font-mono text-[11px]">{r.attempts ?? 0}</span> },
            ]} />
        </div>
      </div>
    ) : <Table rows={null} columns={[]} testid="infra-loading" />}
  </div>);
}

export function AdminWebhooks() {
  const [d] = useResource("/admin/production-checklist");
  const open = d ? d.items.filter((i) => !i.passed) : null;
  return (<div data-testid="page-webhooks">
    <PageHeader kicker="System" title="API / WEBHOOKS" sub="Webhook authenticity is verified server-side. Events update the database — the frontend only reads them." />
    {d && <p className="mb-4 text-[12.5px] text-[#5B6B87]" data-testid="checklist-score">Production readiness: <span className="font-mono font-bold text-[#0B1B33]">{d.passed}/{d.total}</span></p>}
    <Table testid="checklist-table" rows={d ? d.items : null} empty="Checklist unavailable."
      columns={[
        { label: "Check", render: (r) => <span className="font-medium">{r.label}</span> },
        { label: "Status", render: (r) => r.passed ? <span className="text-emerald-700 font-mono text-[11px]">✓ PASS</span> : <span className="text-amber-600 font-mono text-[11px]">OPEN</span> },
        { label: "Detail", render: (r) => <span className="text-[11.5px] text-[#5B6B87]">{r.detail || "—"}</span> },
      ]} />
    {open && open.length > 0 && (
      <div className="adm-card p-5 mt-5" data-testid="webhook-setup-card">
        <p className="adm-kicker mb-2">Webhook Setup</p>
        <p className="text-[12.5px] text-[#43546D]">Register this endpoint in the Resend dashboard → Webhooks:</p>
        <p className="mt-2 font-mono text-[12px] bg-[#F5F7FB] border border-[#E4EAF3] rounded-lg px-4 py-3 select-all" data-testid="webhook-url">https://10x-global.com/api/webhooks/resend</p>
        <p className="mt-2 text-[11.5px] text-[#93A3BC]">Then set RESEND_WEBHOOK_SECRET in the server environment for signature verification.</p>
      </div>
    )}
  </div>);
}

export function AdminSecurity() {
  const [d] = useResource("/admin/overview");
  const attempts = d ? list(d.login_attempts) : null;
  return (<div data-testid="page-security">
    <PageHeader kicker="System" title="SECURITY" sub="RBAC, secure sessions, server-side secrets, rate limiting and brute-force lockouts. No API keys or provider secrets ever reach the browser." />
    {d && <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
      <Stat label="Admin Users" value={d.stats?.users} testid="security-users" />
      <Stat label="Login Attempts Logged" value={attempts?.length ?? 0} testid="security-attempts" />
      <Stat label="Lockouts Active" value={(attempts || []).filter((a) => a.locked_until).length} testid="security-lockouts" />
      <Stat label="Secrets In Frontend" value={0} testid="security-secrets" hint="verified by design" />
    </div>}
    <Table testid="security-table" rows={attempts} empty="No login attempts recorded."
      columns={[
        { label: "Identifier", render: (r) => <span className="font-mono text-[11.5px]">{r.identifier || r.phone || r.email || "—"}</span> },
        { label: "Attempts", render: (r) => <span className="font-mono">{r.attempts ?? r.count ?? "—"}</span> },
        { label: "Locked Until", render: (r) => r.locked_until ? <Pill value="SUPPRESSED" /> : <span className="text-emerald-700 text-[11px] font-mono">CLEAR</span> },
      ]} />
  </div>);
}

export function AdminAudit() {
  const [d] = useResource("/admin/audit");
  return (<div data-testid="page-audit">
    <PageHeader kicker="System" title="AUDIT LOGS" sub="Who did what, when, on which object, with what result. Logins, searches, verifications, invitations, automation changes, approvals." />
    <Table testid="audit-table" rows={d ? d.events : null} empty="No audit events yet."
      columns={[
        { label: "When", render: (r) => <span className="font-mono text-[11px]">{String(r.when || "").slice(5, 19).replace("T", " ")}</span> },
        { label: "Who", render: (r) => <span className="font-mono text-[11px]">{r.who}</span> },
        { label: "What", render: (r) => <Pill value={(r.what || "").split(".")[0].toUpperCase()} /> },
        { label: "Object", render: (r) => <span className="font-mono text-[11px] text-[#7C8AA3]">{r.object || "—"}</span> },
        { label: "Result", render: (r) => <span className="text-[11.5px] text-[#5B6B87] max-w-[360px] block truncate" title={r.result}>{r.result}</span> },
      ]} />
  </div>);
}

export function AdminSettings() {
  const [d] = useResource("/admin/settings");
  return (<div data-testid="page-settings">
    <PageHeader kicker="System" title="SETTINGS" sub="Current acquisition engine configuration. Edit thresholds and automation switches in the Outreach Console." />
    {d ? (
      <div className="adm-card p-5" data-testid="settings-card">
        <pre className="text-[12px] font-mono text-[#43546D] whitespace-pre-wrap">{JSON.stringify(d.settings || d, null, 2)}</pre>
      </div>
    ) : <Table rows={null} columns={[]} testid="settings-loading" />}
  </div>);
}
